import { latestChangelogDate } from './content/changelog'
import { homeCopy } from './content/home'
import { PRIVACY_UPDATED } from './content/privacy'
import { TERMS_UPDATED } from './content/terms'
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  OG_IMAGE,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_WIDTH,
  ORG_CITY,
  ORG_NAME,
  ORG_NUMBER,
  SITE_NAME,
  SITE_URL,
  SUPPORT_EMAIL,
} from './content/site'
import { OG_LOCALE, PAGES, normalisePath, type Lang } from './i18n'

/**
 * Per-page search metadata, in one place.
 *
 * Two consumers read this module, which is why it is plain data rather than JSX:
 * `useSeo` applies it to the live document on client navigation, and
 * `scripts/prerender.mjs` bakes it into each static HTML file at build time. Crawlers
 * that do not run JavaScript only ever see the baked version, so anything added here
 * must work without React.
 *
 * Every page exists in Swedish and English at its own URL, so every page carries the
 * full hreflang set. Getting that wrong is not cosmetic: the English privacy URL is
 * what App Store Connect and Play Console are given.
 */

export type Alternate = { hrefLang: string; path: string }

export type PageSeo = {
  title: string
  description: string
  lang: Lang
  noindex?: boolean
  /** When the page's content last changed meaningfully. Feeds the sitemap's lastmod. */
  dateModified?: string
  jsonLd?: Record<string, unknown>[]
}

const alternatesFor = (key: keyof typeof PAGES): Alternate[] => [
  { hrefLang: 'sv', path: PAGES[key].sv },
  { hrefLang: 'en', path: PAGES[key].en },
  // Swedish is the default for a visitor whose language matches neither, because the
  // site is published in Sweden by a Swedish association.
  { hrefLang: 'x-default', path: PAGES[key].sv },
]

export const OG_IMAGE_ALT: Record<Lang, string> = {
  sv: 'Ronin: en dagbok för vuxna med typ 1-diabetes.',
  en: 'Ronin: a diary for adults with type 1 diabetes.',
}

/**
 * Venueve AB, the company that publishes Ronin. Not an NGO: the association behind
 * diabetesninja.se is a different entity and is deliberately not named on this site.
 * See the note on ORG_NAME in content/site.ts.
 */
export const organization: Record<string, unknown> = {
  '@type': ['Organization', 'Corporation'],
  '@id': `${SITE_URL}/#organization`,
  name: ORG_NAME,
  url: SITE_URL,
  email: SUPPORT_EMAIL,
  address: {
    '@type': 'PostalAddress',
    addressLocality: ORG_CITY,
    addressCountry: 'SE',
  },
  identifier: {
    '@type': 'PropertyValue',
    name: 'Organisationsnummer',
    value: ORG_NUMBER,
  },
}

/** The contact page's own node, so the address is machine-readable as well as visible. */
function contactPage(lang: Lang): Record<string, unknown> {
  return {
    '@type': 'ContactPage',
    '@id': `${SITE_URL}${PAGES.contact[lang]}#page`,
    name: lang === 'sv' ? 'Kontakt' : 'Contact',
    inLanguage: lang,
    publisher: { '@id': `${SITE_URL}/#organization` },
    mainEntity: {
      '@type': 'ContactPoint',
      contactType: lang === 'sv' ? 'kundtjänst' : 'customer support',
      email: SUPPORT_EMAIL,
      availableLanguage: ['sv', 'en'],
    },
  }
}

/**
 * The app itself.
 *
 * `installUrl` is left off entirely if the store URLs are ever emptied: an empty string
 * there is a structured-data error, and a made-up one is worse. No aggregateRating,
 * ever, for the same reason the sibling site refuses one.
 */
function mobileApplication(lang: Lang): Record<string, unknown> {
  const installUrl = [APP_STORE_URL, GOOGLE_PLAY_URL].filter(Boolean)
  return {
    '@type': 'MobileApplication',
    '@id': `${SITE_URL}/#app`,
    name: SITE_NAME,
    description: homeCopy[lang].heroSub,
    applicationCategory: 'HealthApplication',
    operatingSystem: 'iOS, Android',
    inLanguage: ['sv', 'en'],
    url: SITE_URL,
    ...(installUrl.length ? { installUrl } : {}),
    publisher: { '@id': `${SITE_URL}/#organization` },
    featureList: homeCopy[lang].features.map((feature) => feature.title),
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'SEK',
      availability: GOOGLE_PLAY_URL ? 'https://schema.org/InStock' : 'https://schema.org/PreOrder',
    },
  }
}

export const pageSeo: Record<string, PageSeo> = {
  '/': {
    title: 'Ronin – dagbok för vuxna med typ 1-diabetes',
    description:
      'En dagbok där du skriver ner glukos, insulin, kolhydrater och anteckningar. Allt stannar i telefonen. Inget konto, ingen reklam och inga poäng.',
    lang: 'sv',
    dateModified: '2026-10-23',
    jsonLd: [organization, mobileApplication('sv')],
  },
  '/en': {
    title: 'Ronin – a diary for adults with type 1 diabetes',
    description:
      'A diary where you write down glucose, insulin, carbs and notes. Everything stays on your phone. No account, no ads and no scores.',
    lang: 'en',
    dateModified: '2026-10-23',
    jsonLd: [organization, mobileApplication('en')],
  },

  '/kontakt': {
    title: 'Kontakt – Ronin',
    description:
      'Skriv till support@diabetesninja.se om Ronin. En adress, och en människa läser den. Frågor om din behandling går till din vårdkontakt.',
    lang: 'sv',
    dateModified: '2026-08-26',
    jsonLd: [organization, contactPage('sv')],
  },
  '/en/contact': {
    title: 'Contact – Ronin',
    description:
      'Write to support@diabetesninja.se about Ronin. One address, and a person reads it. Questions about your treatment go to your care team.',
    lang: 'en',
    dateModified: '2026-08-26',
    jsonLd: [organization, contactPage('en')],
  },

  '/nyheter': {
    title: 'Nyheter – Ronin',
    description:
      'Vad som är nytt och fixat i Ronin, version för version. Ronin är en dagbok för vuxna med typ 1-diabetes.',
    lang: 'sv',
    dateModified: latestChangelogDate,
  },
  '/en/news': {
    title: "What's new – Ronin",
    description:
      'What is new and fixed in Ronin, version by version. Ronin is a diary for adults with type 1 diabetes.',
    lang: 'en',
    dateModified: latestChangelogDate,
  },

  '/integritet': {
    title: 'Integritet – Ronin',
    description:
      'Allt du skriver ner stannar i telefonen. Ronin har inget konto och ingen server som har din dagbok. Här står vad appen och webbplatsen faktiskt skickar.',
    lang: 'sv',
    dateModified: PRIVACY_UPDATED,
  },
  '/en/privacy': {
    title: 'Privacy – Ronin',
    description:
      'Everything you write down stays on your phone. Ronin has no account and no server that holds your diary. Here is what the app and the site actually send.',
    lang: 'en',
    dateModified: PRIVACY_UPDATED,
  },

  '/villkor': {
    title: 'Villkor – Ronin',
    description:
      'Ronin är en dagbok, inte en medicinteknisk produkt. Appen föreslår aldrig en dos och ger aldrig medicinska råd.',
    lang: 'sv',
    dateModified: TERMS_UPDATED,
  },
  '/en/terms': {
    title: 'Terms and conditions – Ronin',
    description:
      'Ronin is a diary, not a medical device. It never suggests a dose and never gives medical advice.',
    lang: 'en',
    dateModified: TERMS_UPDATED,
  },
}

const NOT_FOUND_SEO: PageSeo = {
  title: 'Sidan finns inte – Ronin',
  description: 'Sidan du letar efter finns inte.',
  lang: 'sv',
  noindex: true,
}

export function seoForPath(pathname: string): PageSeo {
  return pageSeo[normalisePath(pathname)] ?? NOT_FOUND_SEO
}

export function canonicalFor(pathname: string): string {
  const path = normalisePath(pathname)
  return path === '/' ? `${SITE_URL}/` : `${SITE_URL}${path}`
}

/** The page key a path belongs to, for its hreflang set. The 404 has none. */
function alternatesForPath(pathname: string): Alternate[] {
  const path = normalisePath(pathname)
  for (const key of Object.keys(PAGES) as (keyof typeof PAGES)[]) {
    if (PAGES[key].sv === path || PAGES[key].en === path) return alternatesFor(key)
  }
  return []
}

export type TagSpec = {
  tag: 'meta' | 'link'
  attrs: Record<string, string>
}

/**
 * The head tags for a path, as plain data.
 *
 * The single source for both emitters: entry-server.tsx serialises these to HTML at
 * prerender time and hooks/useSeo.ts applies them to the live document on client
 * navigation. Adding a tag here reaches both, so the two cannot drift.
 */
export function headTagSpecs(pathname: string): TagSpec[] {
  const seo = seoForPath(pathname)
  const canonical = canonicalFor(pathname)

  const meta = (kind: 'name' | 'property', key: string, content: string): TagSpec => ({
    tag: 'meta',
    attrs: { [kind]: key, content },
  })

  const specs: TagSpec[] = [
    { tag: 'link', attrs: { rel: 'canonical', href: canonical } },
    meta('property', 'og:title', seo.title),
    meta('property', 'og:description', seo.description),
    meta('property', 'og:url', canonical),
    meta('property', 'og:type', 'website'),
    meta('property', 'og:image', OG_IMAGE),
    meta('property', 'og:image:width', OG_IMAGE_WIDTH),
    meta('property', 'og:image:height', OG_IMAGE_HEIGHT),
    meta('property', 'og:image:alt', OG_IMAGE_ALT[seo.lang]),
    meta('property', 'og:site_name', SITE_NAME),
    meta('property', 'og:locale', OG_LOCALE[seo.lang]),
    meta('name', 'twitter:card', 'summary_large_image'),
    meta('name', 'twitter:title', seo.title),
    meta('name', 'twitter:description', seo.description),
    meta('name', 'twitter:image', OG_IMAGE),
  ]

  if (seo.noindex) specs.push(meta('name', 'robots', 'noindex, follow'))

  for (const alternate of alternatesForPath(pathname)) {
    specs.push({
      tag: 'link',
      attrs: {
        rel: 'alternate',
        hreflang: alternate.hrefLang,
        href: alternate.path === '/' ? `${SITE_URL}/` : `${SITE_URL}${alternate.path}`,
      },
    })
  }

  return specs
}
